import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { isDefined, isNil } from '@trimble-gcs/common';
import {
  catchError,
  concatMap,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { IngestionSource } from '../client-header/client-header.models';
import { ClientHeaderService } from '../client-header/client-header.service';
import { injectLogger } from '../logging/logger-injection';
import { ProjectQuotaService } from '../quota/project-quota.service';
import { ScandataModel } from '../scandata/scandata.models';
import { GET_SCAN_PROJECT_URL } from '../utils/get-scan-project-url';
import { PickerType } from './file-pickers/file-picker.models';
import { PatchImportScan, ResetImportScans } from './import.actions';
import {
  ALLOWED_EXTENSIONS,
  ImportFile,
  ImportFileStatus,
  ImportScan,
  ImportScanError,
  ImportScanStatus,
  ImportStatus,
} from './import.models';
import { ImportState } from './import.state';
import { UploadFile } from './upload.models';
import { UploadService } from './upload.service';

@Injectable({
  providedIn: 'root',
})
export class ImportService {
  private readonly getScanProjectUrl$ = inject(GET_SCAN_PROJECT_URL);
  private readonly logger = injectLogger('ImportService');

  private importSubscription!: Subscription;

  constructor(
    private projectQuotaService: ProjectQuotaService,
    private uploadService: UploadService,
    private store: Store,
    private http: HttpClient,
    private clientHeaderService: ClientHeaderService,
  ) {}

  subscribeImportWatcher() {
    /**
     * When the user initiates an import it could consist of either:
     * 1) a single pointcloud with multiple files or
     * 2) multiple pointclouds each with a single file
     * These pointclouds are refered to as an ImportScan.
     *
     * During the upload/import workflow the ImportScan is updated with
     * progress information, this causes continues emissions from the store.
     *
     * Progress updates are filtered out to prevent starting multiple
     * workflows for the same pointcloud.
     */
    this.importSubscription = this.store
      .select(ImportState.importScans)
      .pipe(
        concatMap((importScans) => {
          // get only new scans (filter out status/progress updates)
          const newImportScans = importScans
            .filter((importScan) => importScan.status === ImportScanStatus.Initialized)
            .map((importScan) => this.updateImportScanStatus(importScan, ImportScanStatus.Pending));

          return forkJoin(newImportScans);
        }),
        filter((importScans) => importScans.length > 0),
        concatMap((importScans) => {
          // get import workflows
          const workflows = importScans.map((importScan) => {
            const isUpload = importScan.files.every(
              (file) => file.pickerType === PickerType.Upload,
            );
            return isUpload ? this.uploadScan(importScan) : this.importScan(importScan);
          });

          return forkJoin(workflows);
        }),
      )
      .subscribe();
  }

  cancelAllImports() {
    return this.store.selectOnce(ImportState.importScans).pipe(
      tap(() => this.importSubscription.unsubscribe()),
      map((importScans) => {
        return importScans.filter(
          (importScan) =>
            isDefined(importScan.scan) && importScan.status !== ImportScanStatus.Completed,
        );
      }),
      concatMap((importScans) => {
        if (importScans.length === 0) return of(null);
        return forkJoin(importScans.map((importScan) => this.deleteScan(importScan)));
      }),
      switchMap(() => this.store.dispatch(new ResetImportScans())),
      tap(() => this.subscribeImportWatcher()),
    );
  }

  private importScan(importScan: ImportScan) {
    return this.checkAvailableQuota(importScan).pipe(
      switchMap((importScan) => this.createPointcloud(importScan)),
      switchMap((importScan) => this.createImport(importScan)),
      switchMap((importScan) =>
        this.updateImportScanStatus(importScan, ImportScanStatus.Completed),
      ),
      catchError(() => this.deleteScan(importScan)),
    );
  }

  private uploadScan(importScan: ImportScan) {
    return this.checkAvailableQuota(importScan).pipe(
      switchMap((importScan) => this.createPointcloud(importScan)),
      switchMap((importScan) => this.createFiles(importScan)),
      switchMap((importScan) => this.uploadFiles(importScan)),
      switchMap((importScan) => this.createIngestion(importScan)),
      switchMap((importScan) =>
        this.updateImportScanStatus(importScan, ImportScanStatus.Completed),
      ),
      catchError(() => this.deleteScan(importScan)),
    );
  }

  private setAndThrowImportScanError(importScan: ImportScan, error: ImportScanError) {
    importScan.status = ImportScanStatus.Error;
    importScan.error = error;

    //mark pending files as skipped
    importScan.files
      .filter((importFile) => importFile.status === ImportFileStatus.Pending)
      .forEach((importFile) => {
        importFile.status = ImportFileStatus.Skipped;
      });

    return this.store.dispatch(new PatchImportScan(importScan)).pipe(
      map(() => {
        throw new Error(error);
      }),
    );
  }

  private updateImportScanStatus(importScan: ImportScan, status: ImportScanStatus) {
    importScan.status = status;
    return this.store.dispatch(new PatchImportScan(importScan)).pipe(map(() => importScan));
  }

  private checkAvailableQuota(importScan: ImportScan): Observable<ImportScan> {
    const importSize = importScan.files.reduce((acc, cur) => acc + cur.size, 0);

    return this.updateImportScanStatus(importScan, ImportScanStatus.CheckQuota).pipe(
      switchMap(() => this.projectQuotaService.quotaExceeded(importSize)),
      catchError((err) => {
        this.logger.error(`Import error checking quota`, {}, err);
        return this.setAndThrowImportScanError(importScan, ImportScanError.CheckQuota);
      }),
      switchMap((quotaExceeded) => {
        if (quotaExceeded) {
          return this.setAndThrowImportScanError(importScan, ImportScanError.QuotaExceeded);
        }

        return of(importScan);
      }),
    );
  }

  private createPointcloud(importScan: ImportScan): Observable<ImportScan> {
    return this.updateImportScanStatus(importScan, ImportScanStatus.CreatePointcloud).pipe(
      switchMap(() => this.getScanProjectUrl$(`/pointclouds`)),
      switchMap((url) => {
        const externalFileId =
          importScan.files.length === 1 ? importScan.files[0].externalFileId : null;

        return this.http.post<ScandataModel>(
          url,
          {
            name: importScan.name,
            externalFileId,
            multiImportsAllowed: false,
            autoIngestAfterImports: true,
            classifier: importScan.classifier,
          },
          {
            headers: this.clientHeaderService.getClientHeaders(IngestionSource.Import),
          },
        );
      }),
      map((scan) => {
        importScan.scan = scan;
        return importScan;
      }),
      catchError((err) => {
        this.logger.error(`Import error creating pointcloud`, {}, err);
        return this.setAndThrowImportScanError(importScan, ImportScanError.CreatePointcloud);
      }),
    );
  }

  private createImport(importScan: ImportScan): Observable<ImportScan> {
    const files = importScan.files.map((file) => ({
      sourceUrl: file.downloadUrl,
      targetFileName: file.name,
      token: file.downloadToken,
    }));

    return this.updateImportScanStatus(importScan, ImportScanStatus.CreateImport).pipe(
      switchMap((importScan) =>
        this.getScanProjectUrl$(`/pointclouds/${importScan.scan!.id}/imports`),
      ),
      switchMap((url) =>
        this.http.post<ImportStatus>(
          url,
          {
            files,
          },
          { headers: this.clientHeaderService.getClientHeaders(IngestionSource.Import) },
        ),
      ),
      map(() => {
        importScan.files.forEach((file) => (file.status = ImportFileStatus.Completed));
        return importScan;
      }),
      catchError((err) => {
        this.logger.error(`Import error creating import`, {}, err);
        return this.setAndThrowImportScanError(importScan, ImportScanError.CreateImport);
      }),
    );
  }

  private createFiles(importScan: ImportScan): Observable<ImportScan> {
    return this.updateImportScanStatus(importScan, ImportScanStatus.CreateFiles).pipe(
      switchMap((importScan) =>
        this.getScanProjectUrl$(`/pointclouds/${importScan.scan!.id}/files`),
      ),
      switchMap((url) => {
        return this.http.post<UploadFile[]>(
          url,
          {
            files: importScan.files.map((importFile) => ({
              name: importFile.name,
              size: importFile.size,
            })),
          },
          {
            headers: this.clientHeaderService.getClientHeaders(IngestionSource.Import),
          },
        );
      }),
      map((uploadFiles) => {
        importScan.files = this.mapImportFileUrl(importScan.files, uploadFiles);
        return importScan;
      }),
      catchError((err) => {
        this.logger.error(`Import error creating files`, {}, err);
        return this.setAndThrowImportScanError(importScan, ImportScanError.CreateFiles);
      }),
    );
  }

  private mapImportFileUrl(importFiles: ImportFile[], uploadFiles: UploadFile[]) {
    return uploadFiles.map((uploadFile) => {
      const importFile = importFiles.find((importFile) => {
        return (
          importFile.name === uploadFile.name &&
          importFile.size === uploadFile.size &&
          isNil(importFile.fileUpload?.uploadUrl)
        );
      })!;

      importFile.fileUpload!.uploadUrl = uploadFile.uploadLink;

      return importFile;
    });
  }

  private uploadFiles(importScan: ImportScan): Observable<ImportScan> {
    return this.updateImportScanStatus(importScan, ImportScanStatus.UploadFiles).pipe(
      switchMap((importScan) => this.uploadService.uploadFiles(importScan.files)),
      map((importFiles) => {
        importScan.files = importFiles;
        return importScan;
      }),
      catchError((err) => {
        this.logger.error(`Error during upload`, {}, err);
        return this.setAndThrowImportScanError(importScan, ImportScanError.UploadFiles);
      }),
    );
  }

  private createIngestion(importScan: ImportScan) {
    return this.updateImportScanStatus(importScan, ImportScanStatus.Ingest).pipe(
      switchMap((importScan) =>
        this.getScanProjectUrl$(`/pointclouds/${importScan.scan!.id}/ingestions`),
      ),
      switchMap((url) =>
        this.http.post(url, {
          headers: this.clientHeaderService.getClientHeaders(IngestionSource.Import),
        }),
      ),
      map(() => importScan),
      catchError((err) => {
        this.logger.error(`Import error creating ingestion`, {}, err);
        return this.setAndThrowImportScanError(importScan, ImportScanError.Ingest);
      }),
    );
  }

  private deleteScan(importScan: ImportScan) {
    if (isNil(importScan.scan)) return of(importScan);

    return this.getScanProjectUrl$(`/pointclouds/${importScan.scan.id}?permanent=true`).pipe(
      switchMap((url) => {
        return this.http.delete<void>(url);
      }),
      catchError((err) => {
        this.logger.error(`Import error deleting pointcloud`, {}, err);
        return of(null);
      }),
      map(() => importScan),
    );
  }
}

export function allowImportForFileType(fileName: string) {
  return ALLOWED_EXTENSIONS.some((extension) =>
    fileName.toLowerCase().endsWith(extension.toLowerCase()),
  );
}
